import { getRoutePermissions } from "../Utilis/permissions";

/**
 * Backend errors come from ApiResponseHelper::setError(), which puts the label
 * ("Validation failed") in `errors` and the validator bag in `message`. So
 * `response.data.message` is a string for plain errors but an object like
 * `{ unitId: ["The selected unit id is invalid."] }` for validation failures —
 * passing it straight to antd's `message.error()` renders an empty toast.
 */
/**
 * Machine-readable fields that share the `errors` bag with real text. Cart
 * quantity failures, for instance, come back as `{ code: "MIN_QUANTITY", … }` —
 * flattening blindly would put that identifier in front of the user.
 */
const NON_MESSAGE_KEYS = new Set(["code", "status", "type", "field", "key"]);

const GENERIC_FORBIDDEN_MESSAGES = new Set([
  "forbidden",
  "this action is unauthorized.",
  "this action is unauthorized",
  "unauthorized",
  "access denied",
  "you do not have permission to perform this action.",
  "you do not have permission to perform this action",
  "you do not have permission to access this page.",
  "you do not have permission to access this page",
]);

const isGenericForbiddenMessage = (message: string): boolean =>
  GENERIC_FORBIDDEN_MESSAGES.has(message.trim().toLowerCase());

const readPermissionField = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const first = value.find((entry) => typeof entry === "string" && entry.trim());
    return typeof first === "string" ? first.trim() : undefined;
  }
  return undefined;
};

/** Resolve the missing permission key from a 403 body or the current FE route. */
export const extractMissingPermission = (
  responseData: any,
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): string | undefined => {
  if (responseData && typeof responseData === "object") {
    const candidates = [
      responseData.permission,
      responseData.required_permission,
      responseData.permission_key,
      responseData.missing_permission,
      responseData.data?.permission,
      responseData.data?.required_permission,
      responseData.data?.permission_key,
      responseData.errors?.permission,
      responseData.errors?.required_permission,
    ];

    for (const candidate of candidates) {
      const permission = readPermissionField(candidate);
      if (permission) {
        return permission;
      }
    }

    // Message already embeds the key: "... (Permission: manage_category_view)"
    const rawMessage =
      typeof responseData.message === "string" ? responseData.message : "";
    const embedded = rawMessage.match(/\(Permission:\s*([^)]+)\)/i);
    if (embedded?.[1]?.trim()) {
      return embedded[1].trim();
    }
  }

  const routePermissions = pathname ? getRoutePermissions(pathname) : [];
  return routePermissions[0];
};

/**
 * User-facing 403 toast / page copy. Prefer the exact permission-aware sentence
 * when a key is known; never surface bare "Forbidden".
 */
export const formatForbiddenErrorMessage = (
  responseData?: any,
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): string => {
  const permission = extractMissingPermission(responseData, pathname);
  if (permission) {
    return `You do not have permission to perform this action (Permission: ${permission})`;
  }

  const rawMessage =
    typeof responseData?.message === "string" ? responseData.message.trim() : "";

  if (rawMessage && !isGenericForbiddenMessage(rawMessage)) {
    return rawMessage;
  }

  return "You do not have permission to perform this action.";
};

const flattenApiError = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const text = flattenApiError(entry);
      if (text) {
        return text;
      }
    }
    return "";
  }

  if (value && typeof value === "object") {
    for (const [key, entry] of Object.entries(
      value as Record<string, unknown>
    )) {
      if (NON_MESSAGE_KEYS.has(key)) {
        continue;
      }
      const text = flattenApiError(entry);
      if (text) {
        return text;
      }
    }
  }

  return "";
};

export const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  const body = flattenApiError(data?.message) || flattenApiError(data?.errors);

  if (body) {
    return body;
  }

  // Callers that read a blob error body themselves rethrow a plain `Error`
  // carrying the backend text, so keep it. An axios rejection's own `message` is
  // "Request failed with status code 403" — never show that to a user.
  const isAxiosError = error?.isAxiosError === true || !!error?.response;
  if (
    !isAxiosError &&
    typeof error?.message === "string" &&
    error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
};
